import React, { useReducer } from 'react';

import classNames from 'classnames';
import InfoIcon from 'icons/info.svg';
import { produce } from 'immer';
import { Trans, useTranslation } from 'react-i18next';
import {
  shareSettingsInitialStore,
  shareSettingsReducer,
} from 'reducers/shareSettingsReducer';

import FilledButton from 'components/application/buttons/FilledButton';
import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CheckboxField from 'components/application/CheckboxField';
import Portal from 'components/application/Portal';
import Tooltip from 'components/application/Tooltip';

import { useBreadBoard } from 'components/contexts/Toaster';

import { masterActivityTradesAttributes } from 'components/helpers/resources/masterActivities';

export default function RiskAssessmentShareSettings({
  masterActivity,
  referrerTradeId,
  selectableTrades,
  tradesUpdatePolicy,
}) {
  const breadboard = useBreadBoard();

  const { t: i18nTCommon } = useTranslation('common');

  const initialStore = produce(shareSettingsInitialStore, (draftStore) => {
    const masterActivityTrades = masterActivity.included.filter(
      (obj) => obj.type === 'masterActivityTrade',
    );
    const associatedTradeIds = masterActivityTrades.map(
      (masterActivityTrade) => masterActivityTrade.relationships.trade.data.id,
    );

    draftStore.domain.masterActivity = masterActivity.data;

    masterActivityTrades.forEach((masterActivityTrade) => {
      draftStore.domain.masterActivityTrades.data[masterActivityTrade.id] =
        masterActivityTrade;
      draftStore.domain.masterActivityTrades.allDataIds.push(
        masterActivityTrade.id,
      );
    });

    selectableTrades.data.forEach((trade) => {
      draftStore.domain.selectableTrades.data[trade.id] = trade;
      draftStore.domain.selectableTrades.allDataIds.push(trade.id);
      draftStore.ui.form[trade.id] = associatedTradeIds.includes(trade.id);
    });
  });

  const [store, dispatch] = useReducer(shareSettingsReducer, initialStore);

  const masterActivityTrades = store.domain.masterActivityTrades.allDataIds.map(
    (masterActivityTradeId) =>
      store.domain.masterActivityTrades.data[masterActivityTradeId],
  );
  const selectableTradeIds = store.domain.selectableTrades.allDataIds;
  const selectedTradeIds = Object.keys(store.ui.form).filter(
    (key) => store.ui.form[key],
  );

  const handleInputChange = (event) => {
    const { target } = event;

    dispatch({ type: 'UPDATE_FORM', key: target.name, value: target.checked });
  };

  const saveChanges = () => {
    const params = {
      master_activity_share_settings: {
        master_activity_trades_attributes: masterActivityTradesAttributes({
          masterActivityTrades,
          selectableTradeIds,
          selectedTradeIds,
        }),
      },
    };

    dispatch({ type: 'UPDATE_SUBMITTING', value: true });

    axios
      .patch(
        `/risk_assessments/${store.domain.masterActivity.id}/share_settings`,
        params,
      )
      .then(() => {
        const tradeId =
          selectedTradeIds.includes(referrerTradeId) ? referrerTradeId : (
            selectedTradeIds[0]
          );

        window.location.href = `/categories/${tradeId}/risk_assessments/${store.domain.masterActivity.id}`;
      })
      .catch(() => {
        dispatch({ type: 'UPDATE_SUBMITTING', value: false });

        breadboard.addInedibleToast();
      });
  };

  return (
    <div className='tw-bg-white tw-pt-7.5'>
      <div className='tw-m-0 tw-mb-6 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
        <div className='tw-mr-3 tw-h-5'>
          <InfoIcon
            className='[&_path]:tw-fill-cyan-800'
            height={20}
            width={20}
          />
        </div>
        <p className='tw-m-0'>
          <Trans
            components={{ span: <span className='tw-font-medium' /> }}
            i18nKey='riskRegister.riskAssessmentShareSettings.components.RiskAssessmentShareSettings.description'
            ns='components'
            tOptions={{ interpolation: { escapeValue: false } }}
            values={{
              masterActivityName: store.domain.masterActivity.attributes.name,
            }}
          />
        </p>
      </div>
      <div className='header-bar header-bar--border header-bar--border-top-curved tw-border-grey-100 tw-bg-grey-050'>
        <span className='tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
          {i18nTCommon('shared.category')}
        </span>
      </div>
      {store.domain.selectableTrades.allDataIds.map((tradeId) => {
        const isSelected = store.ui.form[tradeId];
        const isChangeDisallowed = !tradesUpdatePolicy[tradeId];
        const isDisabled =
          (isSelected && selectedTradeIds.length === 1) || isChangeDisallowed;

        return (
          <div
            className='row-bar row-bar--border-top-none row-bar--flex-align-items-ie-fix tw-border-grey-100 hover:tw-bg-grey-025'
            key={tradeId}
          >
            <div className='row-bar__content'>
              <div
                className={classNames(
                  'tooltip-parent',
                  isChangeDisallowed && 'tooltip-parent--disallowed',
                )}
              >
                <CheckboxField
                  checked={isSelected}
                  disabled={isDisabled}
                  height='short'
                  labelTextProps={{ className: 'tw-font-medium' }}
                  name={tradeId}
                  onChange={handleInputChange}
                  value={tradeId}
                />
                {isChangeDisallowed && (
                  <Tooltip
                    placement='top'
                    tooltip={i18nTCommon(
                      'riskRegister.noAccessToAllDivisionsUsingCategory',
                    )}
                    trigger='hover'
                  />
                )}
              </div>
              <span className='row-bar__weighted-text truncated-text-container tw-ml-4.5 tw-font-medium'>
                {store.domain.selectableTrades.data[tradeId].attributes.name}
              </span>
            </div>
          </div>
        );
      })}
      <Portal containerSelector='.form-actions-right'>
        <OutlinedButton
          color='blue-grey'
          href={`/categories/${referrerTradeId}/risk_assessments/${store.domain.masterActivity.id}`}
          onDark={true}
          size='md'
        >
          {i18nTCommon('shared.cancel')}
        </OutlinedButton>
      </Portal>
      <Portal containerSelector='.form-actions-right'>
        <FilledButton
          color='mint'
          disabled={store.ui.submitting}
          onClick={saveChanges}
          onDark={true}
        >
          {i18nTCommon('shared.saveChanges')}
        </FilledButton>
      </Portal>
    </div>
  );
}
